import type { ShortcutMap } from '@vueron/kiska/provider';
import { isIOS, isMacOS } from '@vueron/kiska/util';

const isAppleDevice = isMacOS() || isIOS();

export enum SHORTCUT_NAMESPACE {
  DEFAULT = 'default',
  LABELING = 'labeling',
}

export const DEFAULT_COMMAND_TREE = {} as const;

export const LABELING_COMMAND_TREE = {
  history: ['undo', 'redo', 'revert'],
  label: [
    'changeLabel1',
    'changeLabel2',
    'changeLabel3',
    'changeLabel4',
    'changeLabel5',
    'changeLabel6',
    'changeLabel7',
    'changeLabel8',
    'changeLabel9',
    'changeLabel10',
  ],
  labelBox: [
    'copyBox',
    'cutBox',
    'pasteBox',
    'generationMode',
    'generate',
    'add',
    'generationMode1',
    'generationMode2',
    'generationMode3',
    'generationMode4',
    'generationMode5',
    'generationMode6',
    'generationMode7',
    'generationMode8',
    'generationMode9',
    'generationMode10',
    'select',
    'selectAll',
    'hide',
    'show',
    'removeBox',
    'arrowToggle',
    'trackIDToggle',
    'isShortTrackID',
    'indexToggle',
    'onlySelectedBoxToggle',
    'rotateClockwise',
    'rotateCounterClockwise',
    'rotate90Degree',
    'rotate180Degree',
    'move-X',
    'move+X',
    'move-Y',
    'move+Y',
    'move-Z',
    'move+Z',
    'decreaseHeight',
    'increaseHeight',
    'decreaseDepth',
    'increaseDepth',
    'transform',
    // 'transformDimension',
    // 'transformRotation',
    // 'transformPosition',
    'trackIDSearch',
    'trackIDCopy',
    'trackIDPaste',
    'trackIDUpdate',
    'focusPrevBox',
    'focusNextBox',
  ],
  boxValidation: ['toggle'],
  reviewData: ['toggle'],
  boxMoveLock: ['toggle'],
  frame: ['prevFrame', 'nextFrame', 'playOrStop', 'goFirst', 'goLast'],
  screen: [
    'changeView',
    'changeView1',
    'changeView2',
    'changeView3',
    'changeView4',
    'fullScreen',
  ],
  toolMenu: ['preAnnotation', 'aiAssist', 'deselectAll'],
  grid: ['show', 'hide', 'toggle'],
  cameraPannel: ['hide', 'show', 'toggle'],
  settingPopup: ['hide', 'show', 'toggle'],
  frameCamImagePopup: ['hide', 'show', 'toggle'],
  shortcutPopup: ['toggle'],
  sync: ['saving', 'saved'],
  start: ['frame'],
} as const;

type DefaultCommandGroup = keyof typeof DEFAULT_COMMAND_TREE;
type DefaultCommand = {
  [K in DefaultCommandGroup]: `${SHORTCUT_NAMESPACE.DEFAULT}.${K}.${(typeof DEFAULT_COMMAND_TREE)[K][number]}`;
}[DefaultCommandGroup];

type LabelingCommandGroup = keyof typeof LABELING_COMMAND_TREE;
type LabelingCommand = {
  [K in LabelingCommandGroup]: `${SHORTCUT_NAMESPACE.LABELING}.${K}.${(typeof LABELING_COMMAND_TREE)[K][number]}`;
}[LabelingCommandGroup];

const createCommandMap = <NS extends SHORTCUT_NAMESPACE>(
  namespace: NS,
  tree: Dictionary<readonly string[]>,
) => {
  const commandMap: Dictionary<string> = {};

  Object.entries(tree).forEach(([group, featureList]) => {
    featureList.forEach(feature => {
      const key = `${namespace}.${group}.${feature}`;

      commandMap[key] = key;
    });
  });

  return commandMap as Record<
    NS extends SHORTCUT_NAMESPACE.DEFAULT ? DefaultCommand : LabelingCommand,
    NS extends SHORTCUT_NAMESPACE.DEFAULT ? DefaultCommand : LabelingCommand
  >;
};

const defaultCommandMap = createCommandMap(
  SHORTCUT_NAMESPACE.DEFAULT,
  DEFAULT_COMMAND_TREE,
);
const labelingCommandMap = createCommandMap(
  SHORTCUT_NAMESPACE.LABELING,
  LABELING_COMMAND_TREE,
);

export const SHORTCUT_COMMAND = {
  ...defaultCommandMap,
  ...labelingCommandMap,
} as const;

export const DEFAULT_SHORTCUT_MAP = {
  [SHORTCUT_NAMESPACE.DEFAULT]: [],
  [SHORTCUT_NAMESPACE.LABELING]: [
    // NOTE: AI 실행
    {
      key: ['shift+a'],
      command: SHORTCUT_COMMAND['labeling.toolMenu.preAnnotation'],
      title: 'shortcut.annotation.limburgite',
      category: 'shortcut.categoryAI.paleophytology',
      description: '',
      inactivated: false,
    },
    {
      key: ['shift+s'],
      command: SHORTCUT_COMMAND['labeling.toolMenu.aiAssist'],
      category: 'shortcut.categoryAI.paleophytology',
      title: 'shortcut.assist.counteralliance',
      description: '',
      inactivated: false,
    },
    // NOTE: 오브젝트 편집
    {
      key: ['enter'],
      command: SHORTCUT_COMMAND['labeling.start.frame'],
      title: 'shortcut.startWork.brulee',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },
    {
      key: ['n'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode'],
      title: 'shortcut.addLabeling.unneighborliness',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },
    {
      key: ['1'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode1'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      title: 'shortcut.labelCar.capeline',
      inactivated: false,
    },
    {
      key: ['2'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode2'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelTruck.carbomethene',
      description: '',
      inactivated: false,
    },
    {
      key: ['3'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode3'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelPedestrian.caroline',
      description: '',
      inactivated: false,
    },
    {
      key: ['4'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode4'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelCyclist.category',
      description: '',
      inactivated: false,
    },
    {
      key: ['5'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode5'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelVan.causewayman',
      description: '',
      inactivated: false,
    },
    {
      key: ['6'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode6'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelBus.censerless',
      description: '',
      inactivated: false,
    },
    {
      key: ['7'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode7'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelPedestrian.caroline',
      description: '',
      inactivated: false,
    },
    {
      key: ['8'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode8'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelCyclist.category',
      description: '',
      inactivated: false,
    },
    {
      key: ['9'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode9'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelVan.causewayman',
      description: '',
      inactivated: false,
    },
    {
      key: ['0'],
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode10'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.labelBus.censerless',
      description: '',
      inactivated: false,
    },

    // NOTE: 선택된 박스의 클래스 변경. n 번째 super class 로 변경합니다.
    {
      key: ['alt+1'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel1'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass1.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+2'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel2'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass2.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+3'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel3'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass3.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+4'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel4'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass4.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+5'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel5'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass5.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+6'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel6'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass6.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+7'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel7'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass7.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+8'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel8'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass8.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+9'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel9'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass9.title',
      description: '',
      inactivated: false,
    },
    {
      key: ['alt+0'],
      command: SHORTCUT_COMMAND['labeling.label.changeLabel10'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.changeClass10.title',
      description: '',
      inactivated: false,
    },
    // {
    //   key: [],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.add'],
    //   description: '',
    //   inactivated: false,
    // },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.select'],
      title: 'shortcut.selectMode.wankapin',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+a'] : ['ctrl+a'],
      command: SHORTCUT_COMMAND['labeling.labelBox.selectAll'],
      title: 'shortcut.selectAll.antihemorrhagic',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },
    {
      key: ['esc'],
      command: SHORTCUT_COMMAND['labeling.toolMenu.deselectAll'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.deslectAll.cesarevitch',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+c'] : ['ctrl+c'],
      command: SHORTCUT_COMMAND['labeling.labelBox.copyBox'],
      title: 'shortcut.copyBox.birdbanding',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+v'] : ['ctrl+v'],
      command: SHORTCUT_COMMAND['labeling.labelBox.pasteBox'],
      title: 'shortcut.pasteBox.praenominal',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },

    {
      key: ['r'],
      command: SHORTCUT_COMMAND['labeling.labelBox.rotateCounterClockwise'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.rotateLeftTen.clasmatocyte',
      description: '',
      inactivated: false,
    },
    {
      key: ['f'],
      command: SHORTCUT_COMMAND['labeling.labelBox.rotateClockwise'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.rotateRightTen.bacteriotropic',
      description: '',
      inactivated: false,
    },

    {
      key: ['w'],
      command: SHORTCUT_COMMAND['labeling.labelBox.move+Z'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.moveUp.travertine',
      description: '',
      inactivated: false,
    },
    {
      key: ['s'],
      command: SHORTCUT_COMMAND['labeling.labelBox.move-Z'],
      title: 'shortcut.moveDown.sirrah',
      category: 'shortcut.categoryObjectEdit.ineconomy',
      description: '',
      inactivated: false,
    },

    {
      key: ['tab'],
      command: SHORTCUT_COMMAND['labeling.labelBox.rotate90Degree'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.roateHalfPi.afrikanderdom',
      description: '',
      inactivated: false,
    },
    {
      key: ['q'],
      command: SHORTCUT_COMMAND['labeling.labelBox.rotate180Degree'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.rotatePi.tepidarium',
      description: '',
      inactivated: false,
    },

    {
      key: ['a'],
      command: SHORTCUT_COMMAND['labeling.labelBox.move-X'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.moveLeft.readmeasurement',
      description: '',
      inactivated: false,
    },
    {
      key: ['d'],
      command: SHORTCUT_COMMAND['labeling.labelBox.move+X'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.moveRight.bathmotropism',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.move+Y'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.moveRightFront.gesticulatory',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.move-Y'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.moveLeftFront.horsecar',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.decreaseHeight'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.decreaseHeight.semiconductor',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.increaseHeight'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.increaseHeight.dominion',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.decreaseDepth'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.decreaseDepth.resought',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.increaseDepth'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.increaseDepth.escherichia',
      description: '',
      inactivated: false,
    },

    // {
    //   key: ['ctrl+x'],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.cut'],
    //   category: 'shortcut.categoryObjectEdit.ineconomy',
    //   title: 'shortcut.cut.chamaesaura',
    //   description: '',
    //   inactivated: false,
    // },

    // NOTE: 오브젝트 조작 도구 (transfrom control)
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.transform'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.sizeTransfornControl.hematospectroscope',
      description: '',
      inactivated: false,
    },
    // {
    //   key: ['ctrl+q'],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.transformDimension'],
    //   category: 'shortcut.categoryTransformControl.comprehensibility',
    //   title: 'shortcut.sizeTransfornControl.hematospectroscope',
    //   description: '',
    //   inactivated: false,
    // },
    // {
    //   key: ['ctrl+w'],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.transformPosition'],
    //   category: 'shortcut.categoryTransformControl.comprehensibility',
    //   title: 'shortcut.positionTransfornControl.kissproof',
    //   description: '',
    //   inactivated: false,
    // },
    // {
    //   key: ['ctrl+e'],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.transformRotation'],
    //   category: 'shortcut.categoryTransformControl.comprehensibility',
    //   title: 'shortcut.rotateTransformControl.forgery',
    //   description: '',
    //   inactivated: false,
    // },

    // NOTE: 작업 제어
    {
      key: isAppleDevice ? ['cmd+z'] : ['ctrl+z'],
      command: SHORTCUT_COMMAND['labeling.history.undo'],
      category: 'shortcut.categoryWorkControl.talkiness',
      title: 'shortcut.undo.nidulus',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+y'] : ['ctrl+y'],
      command: SHORTCUT_COMMAND['labeling.history.redo'],
      category: 'shortcut.categoryWorkControl.talkiness',
      title: 'shortcut.redo.subtectal',
      description: '',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.history.revert'],
      category: 'shortcut.categoryWorkControl.talkiness',
      title: 'shortcut.reset.viscerotonic',
      description: '',
      inactivated: false,
    },
    {
      key: ['x'],
      command: SHORTCUT_COMMAND['labeling.labelBox.removeBox'],
      category: 'shortcut.categoryWorkControl.talkiness',
      title: 'shortcut.removeBox.browntail',
      description: 'shortcut.removeBox.browntail',
      inactivated: false,
    },

    // NOTE: 툴바 기능

    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.boxValidation.toggle'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.boxValidationToggleTitle.fillowite',
      description: 'shortcut.boxValidationToggleDescription.finchery',
      inactivated: false,
      alwaysActived: true,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.reviewData.toggle'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.reviewPannelToggle.complect',
      description: '',
      inactivated: false,
      alwaysActived: true,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.boxMoveLock.toggle'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.boxMoveLockToggle.title',
      description: 'shortcut.boxMoveLockToggle.description',
      inactivated: false,
      alwaysActived: true,
    },
    {
      key: ['v'],
      command: SHORTCUT_COMMAND['labeling.screen.changeView'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.view.trisonant',
      description: '',
      inactivated: false,
    },
    {
      key: ['v+1'],
      command: SHORTCUT_COMMAND['labeling.screen.changeView1'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.view.evaporometer',
      description: '',
      inactivated: false,
    },
    {
      key: ['v+2'],
      command: SHORTCUT_COMMAND['labeling.screen.changeView2'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.view.exhilarator',
      description: '',
      inactivated: false,
    },
    {
      key: ['v+3'],
      command: SHORTCUT_COMMAND['labeling.screen.changeView3'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.view.exodus',
      description: '',
      inactivated: false,
    },
    {
      key: ['v+4'],
      command: SHORTCUT_COMMAND['labeling.screen.changeView4'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.view.falconbill',
      description: '',
      inactivated: false,
    },

    // {
    //   key: [],
    //   command: SHORTCUT_COMMAND['labeling.cameraPannel.hide'],
    //   category: 'shortcut.categoryToolbar.impasture',
    //   // title: ""
    //   description: '',
    //   inactivated: false,
    // },
    // {
    //   key: [],
    //   command: SHORTCUT_COMMAND['labeling.cameraPannel.show'],
    //   category: 'shortcut.categoryToolbar.impasture',
    //   // title: ""
    //   description: '',
    //   inactivated: false,
    // },
    // {
    //   key: [],
    //   command: SHORTCUT_COMMAND['labeling.cameraPannel.toggle'],
    //   category: 'shortcut.categoryToolbar.impasture',
    //   title: 'shortcut.cameraPannelToggle.coacceptor',
    //   description: '',
    //   inactivated: false,
    // },

    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.frameCamImagePopup.toggle'],
      title: 'shortcut.frameCamImagePopup.condignity',
      category: 'shortcut.categoryToolbar.impasture',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+enter'] : ['ctrl+enter'],
      command: SHORTCUT_COMMAND['labeling.screen.fullScreen'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.fullScreen.subhirsute',
      description: '',
      inactivated: false,
    },
    // {
    //   key: [],
    //   command: SHORTCUT_COMMAND['labeling.settingPopup.hide'],
    //   category: 'shortcut.categoryToolbar.impasture',
    //   // title: 'shortcut.fullScreen.subhirsute'
    //   description: '',
    //   inactivated: false,
    // },
    // {
    //   key: [],
    //   command: SHORTCUT_COMMAND['labeling.settingPopup.show'],
    //   category: 'shortcut.categoryToolbar.impasture',
    //   // title:
    //   description: '',
    //   inactivated: false,
    // },

    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.settingPopup.toggle'],
      category: 'shortcut.categoryToolbar.impasture',
      title: 'shortcut.settingPopUpToggle.cofoundress',
      description: '',
      inactivated: false,
    },

    // NOTE: 환경 설정
    {
      key: ['g'],
      command: SHORTCUT_COMMAND['labeling.grid.toggle'],
      category: 'shortcut.categoryPreferences.chylificatory',
      title: 'shortcut.gridToggle.churlish',
      description: '',
      inactivated: false,
    },
    {
      key: ['u'],
      command: SHORTCUT_COMMAND['labeling.labelBox.arrowToggle'],
      category: 'shortcut.categoryPreferences.chylificatory',
      title: 'shortcut.arrow.planch',
      description: '',
      inactivated: false,
    },
    {
      key: ['m'],
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDToggle'],
      category: 'shortcut.categoryPreferences.chylificatory',
      title: 'shortcut.labelBoxTrackIDTitle.euchroite',
      description: 'shortcut.labelBoxTrackIDDescription.euryprosopic',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.isShortTrackID'],
      category: 'shortcut.categoryPreferences.chylificatory',
      title: 'shortcut.isShortTrackIDTitle.saprolite',
      description: 'shortcut.isShortTrackIDDescription.pithoegia',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.onlySelectedBoxToggle'],
      category: 'shortcut.categoryPreferences.chylificatory',
      title: 'shortcut.onlySelectedBoxToggle.title',
      description: '',
      inactivated: false,
    },
    // {
    //   key: ['f3'],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.hide'],
    //   category: 'shortcut.categoryPreferences.chylificatory',
    //   // title: '
    //   description: '',
    //   inactivated: false,
    // },
    // {
    //   key: ['f3'],
    //   command: SHORTCUT_COMMAND['labeling.labelBox.show'],
    //   category: 'shortcut.categoryPreferences.chylificatory',
    //   // title: '',
    //   description: '',
    //   inactivated: false,
    // },
    {
      key: ['f1'],
      command: SHORTCUT_COMMAND['labeling.shortcutPopup.toggle'],
      category: 'shortcut.categoryPreferences.chylificatory',
      title: 'shortcut.shortcutPopup.conventionist',
      description: '',
      inactivated: false,
    },

    // NOTE: 프레임 제어
    {
      key: ['arrowleft'],
      command: SHORTCUT_COMMAND['labeling.frame.prevFrame'],
      category: 'shortcut.categoryFrameControl.basioccipital',
      title: 'shortcut.prevFrame.chlorocresol',
      description: '',
      inactivated: false,
    },
    {
      key: ['arrowright'],
      command: SHORTCUT_COMMAND['labeling.frame.nextFrame'],
      category: 'shortcut.categoryFrameControl.basioccipital',
      title: 'shortcut.nextFrame.choree',
      description: '',
      inactivated: false,
    },
    {
      key: ['space'],
      command: SHORTCUT_COMMAND['labeling.frame.playOrStop'],
      category: 'shortcut.categoryFrameControl.basioccipital',
      title: 'shortcut.framePlay.tripeman',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+arrowleft'] : ['ctrl+arrowleft'],
      command: SHORTCUT_COMMAND['labeling.frame.goFirst'],
      category: 'shortcut.categoryFrameControl.basioccipital',
      title: 'shortcut.firstFrame.choreomania',
      description: '',
      inactivated: false,
    },
    {
      key: isAppleDevice ? ['cmd+arrowright'] : ['ctrl+arrowright'],
      command: SHORTCUT_COMMAND['labeling.frame.goLast'],
      category: 'shortcut.categoryFrameControl.basioccipital',
      title: 'shortcut.lastFrame.chumulu',
      description: '',
      inactivated: false,
    },

    {
      key: isAppleDevice ? ['cmd+shift+f'] : ['ctrl+shift+f'],
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDSearch'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.searchTrackID.abreaction',
      description: 'shortcut.searchTrackID.abreaction',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDCopy'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.copyTrackID.abreast',
      description: 'shortcut.copyTrackID.abreast',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDPaste'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.pasteTrackID.abrenounce',
      description: 'shortcut.pasteTrackID.abrenounce',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDUpdate'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.linkedTrackIDUpdate.analysation',
      description: 'shortcut.linkedTrackIDUpdate.analysation',
      inactivated: false,
    },

    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.focusPrevBox'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.focusPrevBox.abridgment',
      description: 'shortcut.focusPrevBox.abridgment',
      inactivated: false,
    },
    {
      key: [],
      command: SHORTCUT_COMMAND['labeling.labelBox.focusNextBox'],
      category: 'shortcut.categoryObjectEdit.ineconomy',
      title: 'shortcut.focusNextBox.abristle',
      description: 'shortcut.focusNextBox.abristle',
      inactivated: false,
    },
  ],
} as unknown as ShortcutMap<typeof SHORTCUT_NAMESPACE, typeof SHORTCUT_COMMAND>;
